import { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from 'recharts';

// Dashboard pie chart — going_to values as they appear in the LOG FILE
const MARKET_DESTINATIONS = [
  'mdsphy',
  'healthnet',
  'iph',
  'ipbm',
  'cimas',
  'apace',
  'dhs',
  'dhsnam',
];

// Reports → Market Share pie chart — going_to values as stored in the DATABASE
// Edit this list independently to match your tx_details going_to values
const MARKET_SHARE_DESTINATIONS = [
  'mdsphy',
  'healthnet',
  'iph',
  'ipbm',
  'cimas',
  'apace',
  'dhs',
  'dhsnam',
];

// Reports → Transactions destination dropdown — going_to values as stored in the DATABASE
const REPORT_DESTINATIONS = [
  'mdsphy',
  'healthnet',
  'iph',
  'ipbm',
  'cimas',
  'apace',
  'dhs',
  'dhsnam',
];

// Update this list as eScripting vendors change
const ESCRIPTING_VENDORS = [
  'CIMAS',
  'ICAN',
  'CAAPP',
  'BMED',
  'MEDO',
  'CHRONIC',
  'REJECT',
];

// ─── GROUPS ────────────────────────────────────────────────────────────────────

export function Groups() {
  const api = useApi();
  const { user } = useAuth();
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get(`/groups${search ? `?search=${encodeURIComponent(search)}` : ''}`);
      setGroups(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api, search]);

  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setError('');
    try {
      await api.post('/groups', { groupname: newName });
      setNewName(''); setShowAdd(false); load();
    } catch (e) { setError(e.message); }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete group "${name}"? This will unlink all mailboxes.`)) return;
    try { await api.del(`/groups/${id}`); load(); } catch (e) { alert(e.message); }
  };

  const canEdit = user?.groupaccesslevel >= 2;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Groups</h1>
          <div className="page-subtitle">{groups.length} groups</div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="search-bar">
            <span style={{ color: 'var(--text-muted)' }}>⌕</span>
            <input placeholder="Search groups…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          {canEdit && <button className="btn btn-primary" onClick={() => setShowAdd(true)}>+ Add Group</button>}
        </div>
      </div>

      {showAdd && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>New Group</div>
          {error && <div className="alert alert-error">{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <input className="form-input" placeholder="Group name" value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAdd()} autoFocus />
            <button className="btn btn-primary" onClick={handleAdd}>Create</button>
            <button className="btn btn-ghost" onClick={() => setShowAdd(false)}>Cancel</button>
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <table className="data-table">
          <thead><tr><th>Group Name</th><th>Mailboxes</th><th>Account Manager</th><th>Group Manager</th>{canEdit && <th></th>}</tr></thead>
          <tbody>
            {loading ? [...Array(4)].map((_, i) => (
              <tr key={i}>{[...Array(4)].map((_, j) => <td key={j}><div className="skeleton" style={{ height: 14, width: '80%' }} /></td>)}</tr>
            )) : groups.length === 0 ? (
              <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No groups found</td></tr>
            ) : groups.map(g => (
              <tr key={g.groupid}>
                <td style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{g.groupname}</td>
                <td><span className="badge badge-info">{g.mailboxcount}</span></td>
                <td>{g.accountmanageremail || '—'}</td>
                <td>{g.groupmanageremail || '—'}</td>
                {canEdit && (
                  <td>
                    <button className="btn btn-danger btn-sm" onClick={() => handleDelete(g.groupid, g.groupname)}>Delete</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── SERVICES ──────────────────────────────────────────────────────────────────

function ServiceModal({ service, instance, onClose, onSaved }) {
  const api = useApi();
  const isEdit = !!service?.destination;
  const [form, setForm] = useState(service || { destination: '', txtype: '', module: '', function: '', parameters: '', note: '', email: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = f => e => setForm(prev => ({ ...prev, [f]: e.target.value }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      if (isEdit) { await api.put(`/services/${service.destination}`, form); }
      else { await api.post('/services', form); }
      onSaved(); onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? 'Edit Service' : 'Add Service'}</div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div className="grid-2">
            <div className="form-group">
              <label className="form-label">Destination</label>
              <input className="form-input" value={form.destination} onChange={set('destination')} disabled={isEdit} />
            </div>
            <div className="form-group">
              <label className="form-label">Tx Type</label>
              <input className="form-input" value={form.txtype} onChange={set('txtype')} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Module</label>
            <input className="form-input" value={form.module} onChange={set('module')} />
          </div>
          <div className="form-group">
            <label className="form-label">Function</label>
            <input className="form-input" value={form.function} onChange={set('function')} />
          </div>
          <div className="form-group">
            <label className="form-label">Parameters</label>
            <input className="form-input" value={form.parameters} onChange={set('parameters')} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email} onChange={set('email')} />
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="form-textarea" value={form.note} onChange={set('note')} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function Services() {
  const api = useApi();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [cfgPath, setCfgPath] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/services');
      setServices(res.services || []);
      setCfgPath(res.source || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? services.filter(s =>
        s.destination.toLowerCase().includes(search.toLowerCase()) ||
        s.txtype.toLowerCase().includes(search.toLowerCase()) ||
        s.module.toLowerCase().includes(search.toLowerCase())
      )
    : services;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dynamic Services</h1>
          <div className="page-subtitle" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{services.length} services</span>
            {cfgPath && (
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 11,
                color: 'var(--text-muted)', background: 'var(--bg-hover)',
                padding: '2px 8px', borderRadius: 4,
              }}>{cfgPath}</span>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <div className="search-bar">
            <span style={{ color: 'var(--text-muted)' }}>⌕</span>
            <input placeholder="Search services…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <button className="btn btn-secondary btn-sm" onClick={load}>↻ Reload</button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: 16 }}>
          Could not read services file: {error}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>Destination</th>
              <th>Tx Type</th>
              {user?.accesslevel >= 3 && (
                <><th>Module</th><th>Function</th><th>Parameters</th></>
              )}
            </tr>
          </thead>
          <tbody>
            {loading ? [...Array(6)].map((_, i) => (
              <tr key={i}>{[...Array(user?.accesslevel >= 3 ? 5 : 2)].map((_, j) => (
                <td key={j}><div className="skeleton" style={{ height: 12, width: '80%' }} /></td>
              ))}</tr>
            )) : filtered.length === 0 ? (
              <tr>
                <td colSpan={user?.accesslevel >= 3 ? 5 : 2}
                  style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>
                  {search ? 'No services match your search' : 'No services found in config file'}
                </td>
              </tr>
            ) : filtered.map((s, i) => (
              <tr key={i}>
                <td style={{ color: 'var(--accent)', fontWeight: 600 }}>{s.destination}</td>
                <td><span className="badge badge-info">{s.txtype || '—'}</span></td>
                {user?.accesslevel >= 3 && (
                  <>
                    <td>{s.module || '—'}</td>
                    <td>{s.function || '—'}</td>
                    <td style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 300, wordBreak: 'break-all' }}>
                      {s.parameters || '—'}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--text-muted)' }}>
        Services are managed by editing <span style={{ fontFamily: 'var(--font-mono)' }}>DynamicServices.cfg</span> directly on the server. Use ↻ Reload to refresh after making changes.
      </div>
    </div>
  );
}

// ─── USERS ─────────────────────────────────────────────────────────────────────

function UserModal({ userRecord, onClose, onSaved }) {
  const api = useApi();
  const isEdit = !!userRecord?.userid;
  const [form, setForm] = useState(userRecord || {
    userid: '', password: '', name: '', email: '', mobileno: '', software: '',
    accesslevel: 1, mailboxaccesslevel: 0, serviceaccesslevel: 0,
    groupaccesslevel: 0, fileuploadaccesslevel: 0, reportaccesslevel: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      if (isEdit) { await api.put(`/users/${form.userid}`, form); }
      else { await api.post('/users', form); }
      onSaved(); onClose();
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const LVL = [0, 1, 2, 3, 4];

  return createPortal(
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <div className="modal-title">{isEdit ? `Edit User — ${form.userid}` : 'Add User'}</div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">User ID</label>
            <input className="form-input" value={form.userid} onChange={set('userid')} disabled={isEdit} />
          </div>
          <div className="form-group">
            <label className="form-label">{isEdit ? 'New Password (leave blank to keep)' : 'Password'}</label>
            <input className="form-input" type="password" value={form.password || ''} onChange={set('password')} />
          </div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}>
            <label className="form-label">Full Name</label>
            <input className="form-input" value={form.name || ''} onChange={set('name')} />
          </div>
          <div className="form-group">
            <label className="form-label">Email</label>
            <input className="form-input" type="email" value={form.email || ''} onChange={set('email')} />
          </div>
          <div className="form-group">
            <label className="form-label">Mobile</label>
            <input className="form-input" value={form.mobileno || ''} onChange={set('mobileno')} />
          </div>
          {[
            { field: 'accesslevel', label: 'Access Level' },
            { field: 'mailboxaccesslevel', label: 'Mailbox Access' },
            { field: 'serviceaccesslevel', label: 'Services Access' },
            { field: 'groupaccesslevel', label: 'Groups Access' },
            { field: 'fileuploadaccesslevel', label: 'File Upload' },
            { field: 'reportaccesslevel', label: 'Reports Access' },
          ].map(({ field, label }) => (
            <div className="form-group" key={field}>
              <label className="form-label">{label}</label>
              <select className="form-select" value={form[field] || 0} onChange={set(field)}>
                {LVL.map(v => <option key={v} value={v}>{v} {v === 0 ? '(none)' : v === 4 ? '(admin)' : ''}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>{loading ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function Users() {
  const api = useApi();
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setUsers(await api.get('/users')); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (u) => {
    if (!window.confirm(`Delete user "${u.name || u.userid}"? This cannot be undone.`)) return;
    try {
      await api.del(`/users/${u.userid}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  if (user?.accesslevel < 3) {
    return <div className="alert alert-error">Insufficient access level to view users.</div>;
  }

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Users</h1>
          <div className="page-subtitle">{users.length} user profiles</div>
        </div>
        <button className="btn btn-primary" onClick={() => setModal('add')}>+ Add User</button>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <table className="data-table">
          <thead>
            <tr>
              <th>User ID</th><th>Name</th><th>Email</th><th>Access</th>
              <th>Mailbox</th><th>Services</th><th>Groups</th><th>Reports</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? [...Array(4)].map((_, i) => (
              <tr key={i}>{[...Array(8)].map((_, j) => <td key={j}><div className="skeleton" style={{ height: 12, width: '70%' }} /></td>)}</tr>
            )) : users.length === 0 ? (
              <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No users found</td></tr>
            ) : users.map(u => (
              <tr key={u.userid}>
                <td style={{ color: 'var(--accent)', fontWeight: 600 }}>{u.userid}</td>
                <td style={{ color: 'var(--text-primary)' }}>{u.name}</td>
                <td>{u.email || '—'}</td>
                <td><span className="badge badge-info">L{u.accesslevel}</span></td>
                <td>{u.mailboxaccesslevel}</td>
                <td>{u.serviceaccesslevel}</td>
                <td>{u.groupaccesslevel}</td>
                <td>{u.reportaccesslevel}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setModal(u)}>Edit</button>
                    {user?.accesslevel >= 4 && u.userid !== user?.userid && (
                      <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u)}>Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && <UserModal userRecord={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}

// ─── REPORTS ───────────────────────────────────────────────────────────────────

// ErxTable — eScripting pivot table with expandable pharmacy rows
function ErxTable({ mailboxRows, vendors, vendorTotals, grandTotal, erxFrom, erxTo, api, buildErxParams }) {
  const [expanded, setExpanded] = useState({});
  const [txData, setTxData] = useState({});
  const [txLoading, setTxLoading] = useState({});

  const toggleRow = async (key, mailboxName) => {
    const isOpen = expanded[key];
    setExpanded(prev => ({ ...prev, [key]: !isOpen }));

    // Fetch transactions for this mailbox if not already loaded
    if (!isOpen && !txData[key]) {
      setTxLoading(prev => ({ ...prev, [key]: true }));
      try {
        const params = buildErxParams();
        // Find the mailboxid from erxData via mailbox name match
        const rows = await api.get(`/reports/erxres/transactions?${params}&mailbox_name=${encodeURIComponent(mailboxName)}`);
        setTxData(prev => ({ ...prev, [key]: rows }));
      } catch (e) {
        setTxData(prev => ({ ...prev, [key]: [] }));
      } finally {
        setTxLoading(prev => ({ ...prev, [key]: false }));
      }
    }
  };

  const colSpan = vendors.length + 3; // expand + mailbox + bhf + vendors + total

  return (
    <table className="data-table">
      <thead>
        <tr>
          <th style={{ width: 32 }}></th>
          <th>Mailbox</th>
          <th>BHF</th>
          {vendors.map(v => <th key={v} style={{ textAlign: 'right' }}>{v}</th>)}
          <th style={{ textAlign: 'right', color: 'var(--accent)' }}>Total</th>
        </tr>
      </thead>
      <tbody>
        {mailboxRows.map((m, i) => {
          const key = m.mailbox_name || m.bhf || i;
          const rowTotal = vendors.reduce((s, v) => s + (m.vendors[v] || 0), 0);
          const isOpen = !!expanded[key];
          const txRows = txData[key] || [];
          const loading = txLoading[key];

          return (
            <>
              <tr key={key} style={{ cursor: 'pointer' }} onClick={() => toggleRow(key, m.mailbox_name)}>
                <td style={{ textAlign: 'center', color: 'var(--accent)', fontSize: 11, userSelect: 'none' }}>
                  {isOpen ? '▾' : '▸'}
                </td>
                <td style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{m.mailbox_name || '—'}</td>
                <td style={{ fontFamily: 'var(--font-mono)' }}>{m.bhf || '—'}</td>
                {vendors.map(v => (
                  <td key={v} style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: m.vendors[v] ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                    {m.vendors[v] ? m.vendors[v].toLocaleString() : '—'}
                  </td>
                ))}
                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)' }}>
                  {rowTotal.toLocaleString()}
                </td>
              </tr>

              {isOpen && (
                <tr key={`${key}-expand`}>
                  <td colSpan={colSpan} style={{ padding: 0, background: 'var(--bg-base)' }}>
                    <div style={{ padding: '8px 16px 12px 40px' }}>
                      {loading ? (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>Loading transactions…</div>
                      ) : txRows.length === 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--text-muted)', padding: '8px 0' }}>No transaction detail available</div>
                      ) : (
                        <table className="data-table" style={{ fontSize: 12 }}>
                          <thead>
                            <tr>
                              <th>Date/Time</th>
                              <th>Vendor</th>
                              <th>Ref No</th>
                              <th style={{ textAlign: 'right' }}>Items</th>
                              <th style={{ textAlign: 'right' }}>Claim (R)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {txRows.slice(0, 100).map((tx, j) => (
                              <tr key={j}>
                                <td style={{ whiteSpace: 'nowrap' }}>{new Date(tx.txdatetime).toLocaleString()}</td>
                                <td>{tx.going_to_sub || '—'}</td>
                                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{tx.refno || '—'}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>{tx.items ?? '—'}</td>
                                <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                                  {tx.claim ? `${parseFloat(tx.claim).toFixed(2)}` : '—'}
                                </td>
                              </tr>
                            ))}
                            {txRows.length > 100 && (
                              <tr><td colSpan={4} style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 11 }}>
                                Showing first 100 of {txRows.length} transactions
                              </td></tr>
                            )}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </td>
                </tr>
              )}
            </>
          );
        })}

        {/* Grand total row */}
        <tr style={{ background: 'var(--bg-hover)', borderTop: '2px solid var(--border-bright)' }}>
          <td></td>
          <td style={{ fontWeight: 700, color: 'var(--text-primary)' }} colSpan={2}>TOTAL</td>
          {vendors.map(v => (
            <td key={v} style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)' }}>
              {vendorTotals[v].toLocaleString()}
            </td>
          ))}
          <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent)', fontSize: 14 }}>
            {grandTotal.toLocaleString()}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

// Client-side Excel export using SheetJS (loaded from CDN via dynamic import)
// sheets: [{ name: 'Sheet1', rows: [...] }, ...]
async function exportToExcel(sheets, filename) {
  if (!window.XLSX) {
    await new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
      s.onload = resolve;
      s.onerror = reject;
      document.head.appendChild(s);
    });
  }
  const XLSX = window.XLSX;
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, name);
  });
  XLSX.writeFile(wb, filename);
}

export function Reports() {
  const api = useApi();
  const { token, user } = useAuth();
  const [tab, setTab] = useState('transactions');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fromdate, setFrom] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [todate, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [mailboxid, setMailboxId] = useState('');
  const [destination, setDestination] = useState('');
  const [txtype, setTxtype] = useState('');
  const [scheme, setScheme] = useState('');

  const buildParams = useCallback(() => {
    const params = new URLSearchParams({ fromdate, todate });
    if (mailboxid)   params.set('mailboxid', mailboxid);
    if (destination) params.set('destination', destination);
    if (txtype)      params.set('txtype', txtype);
    if (scheme)      params.set('scheme', scheme);
    return params;
  }, [fromdate, todate, mailboxid, destination, txtype, scheme]);

  const load = useCallback(async () => {
    setLoading(true);
    setData([]);
    try {
      let endpoint = '';
      const params = buildParams();
      if (tab === 'transactions') endpoint = `/reports/transactions?${params}`;
      else if (tab === 'errors') endpoint = `/reports/error-log`;
      setData(await api.get(endpoint));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api, tab, buildParams]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const params = buildParams();
      // Fetch CSV from server — bypasses SheetJS memory limits entirely
      const res = await fetch(`/api/reports/transactions/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `transactions_${fromdate}_to_${todate}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setExporting(false);
    }
  }, [token, buildParams, fromdate, todate]);

  const [erxData, setErxData] = useState([]);
  const [erxLoading, setErxLoading] = useState(false);
  const [erxExporting, setErxExporting] = useState(false);
  const [erxFrom, setErxFrom] = useState(new Date(Date.now() - 86400000).toISOString().slice(0, 10));
  const [erxTo, setErxTo] = useState(new Date().toISOString().slice(0, 10));
  const [erxVendor, setErxVendor] = useState('');

  const buildErxParams = useCallback(() => {
    const params = new URLSearchParams({ fromdate: erxFrom, todate: erxTo });
    if (erxVendor) params.set('vendor', erxVendor);
    return params;
  }, [erxFrom, erxTo, erxVendor]);

  const loadErx = useCallback(async () => {
    setErxLoading(true);
    setErxData([]);
    try {
      const rows = await api.get(`/reports/erxres?${buildErxParams()}`);
      setErxData(rows);
    } catch (e) { console.error(e); }
    finally { setErxLoading(false); }
  }, [api, buildErxParams]);

  const handleErxExport = useCallback(async () => {
    setErxExporting(true);
    try {
      // Fetch both summary and full detail in parallel
      const [rows, detailRows] = await Promise.all([
        api.get(`/reports/erxres/export?${buildErxParams()}`),
        api.get(`/reports/erxres/transactions?${buildErxParams()}`),
      ]);

      // ── Summary sheet — pivot: Mailbox × Vendor with TOTAL last ────────────
      const vendors = [...new Set(rows.map(r => r.vendor))].sort();
      const mailboxMap = {};
      rows.forEach(r => {
        const key = r.mailbox_name || r.bhf || '';
        if (!mailboxMap[key]) mailboxMap[key] = { bhf: r.bhf || '' };
        mailboxMap[key][r.vendor] = r.count;
      });
      const summaryRows = Object.entries(mailboxMap).map(([mailbox, data]) => {
        const row = { 'Mailbox': mailbox, 'BHF': data.bhf };
        vendors.forEach(v => { row[v] = Number(data[v]) || 0; });
        row['TOTAL'] = vendors.reduce((s, v) => s + (Number(data[v]) || 0), 0);
        return row;
      });
      const grandTotal = { 'Mailbox': 'TOTAL', 'BHF': '' };
      vendors.forEach(v => {
        grandTotal[v] = summaryRows.reduce((s, r) => s + (Number(r[v]) || 0), 0);
      });
      grandTotal['TOTAL'] = summaryRows.reduce((s, r) => s + r['TOTAL'], 0);
      summaryRows.push(grandTotal);

      // ── Detail sheet — one row per transaction ──────────────────────────────
      const detailClean = detailRows.map(r => ({
        'Pharmacy':   r.mailbox_name || '',
        'Date/Time':  r.txdatetime,
        'Vendor':     r.going_to_sub || '',
        'Ref No':     r.refno || '',
        'Items':      r.items ?? '',
        'Claim (R)':  r.claim ? parseFloat(r.claim).toFixed(2) : '',
      }));

      await exportToExcel([
        { name: 'Summary',  rows: summaryRows  },
        { name: 'Detail',   rows: detailClean  },
      ], `escripting_${erxFrom}_to_${erxTo}.xlsx`);

    } catch (e) {
      alert('Export failed: ' + e.message);
    } finally {
      setErxExporting(false);
    }
  }, [api, buildErxParams, erxFrom, erxTo]);

  const TABS = [
    { id: 'transactions',  label: 'Transactions'   },
    { id: 'marketshare',   label: 'Market Share'   },
    { id: 'erxres',        label: 'eScripting'     },
    { id: 'invoice',       label: 'Invoice'        },
    { id: 'errors',        label: 'Error Log'      },
  ];

  // ── Invoice state ────────────────────────────────────────────────────────────
  const now = new Date();
  const [invYear,  setInvYear]  = useState(now.getFullYear());
  const [invMonth, setInvMonth] = useState(now.getMonth() + 1); // 1-based
  const [invDest,  setInvDest]  = useState('ipbm');
  const [invData,  setInvData]  = useState(null);
  const [invLoading, setInvLoading] = useState(false);
  const [invExporting, setInvExporting] = useState(false);
  const [invPdfExporting, setInvPdfExporting] = useState(false);

  const MONTHS = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December'
  ];

  const loadInvoice = useCallback(async () => {
    setInvLoading(true);
    setInvData(null);
    try {
      const data = await api.get(
        `/reports/invoice/data?year=${invYear}&month=${invMonth}&destination=${invDest}`
      );
      setInvData(data);
    } catch (e) { console.error(e); }
    finally { setInvLoading(false); }
  }, [api, invYear, invMonth, invDest]);

  const handleInvExport = useCallback(async () => {
    if (!invData) return;
    setInvExporting(true);
    try {
      const monthName = MONTHS[invData.month - 1];

      // Summary sheet
      const summaryRows = invData.schemes.map(s => ({
        'Scheme':            s.scheme,
        'Plan Name':         s.plan_name || '',
        'Transaction Count': s.total,
        'Rate (R)':          invData.applicableRate.toFixed(4),
        'Fee (R)':           s.fee.toFixed(2),
      }));
      summaryRows.push({
        'Scheme':            'TOTAL',
        'Plan Name':         '',
        'Transaction Count': invData.grandTotal,
        'Rate (R)':          invData.applicableRate.toFixed(4),
        'Fee (R)':           invData.totalFee.toFixed(2),
      });

      // Detail sheet — one row per refno, grouped pharmacy → scheme
      // Sort by pharmacy then scheme then refno
      const sortedDetail = [...invData.detail].sort((a, b) => {
        const p = a.pharmacy_name.localeCompare(b.pharmacy_name);
        if (p !== 0) return p;
        const s = a.scheme.localeCompare(b.scheme);
        if (s !== 0) return s;
        return String(a.refno).localeCompare(String(b.refno));
      });

      const detailRows = sortedDetail.map(d => ({
        'BHF':      d.industryno,
        'Pharmacy': d.pharmacy_name,
        'Scheme':   d.scheme,
        'Ref No':   d.refno,
        'Claim (R)': Number(d.claim).toFixed(2),
      }));

      await exportToExcel([
        { name: 'Invoice Summary', rows: summaryRows },
        { name: 'Pharmacy Detail', rows: detailRows  },
      ], `invoice_${invDest}_${monthName}_${invYear}.xlsx`);
    } catch (e) { alert('Export failed: ' + e.message); }
    finally { setInvExporting(false); }
  }, [invData, invYear, invDest, MONTHS]);

  const handleInvPdfExport = useCallback(async () => {
    if (!invData) return;
    setInvPdfExporting(true);
    try {
      const monthName = MONTHS[invData.month - 1];
      const res = await fetch(
        `/api/reports/invoice/pdf?year=${invData.year}&month=${invData.month}&destination=${invData.destination}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `invoice_${invData.destination}_${monthName}_${invData.year}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) { alert('PDF export failed: ' + e.message); }
    finally { setInvPdfExporting(false); }
  }, [invData, token, MONTHS]);

  const [msFrom, setMsFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [msTo, setMsTo] = useState(new Date().toISOString().slice(0, 10));
  const [msDestData, setMsDestData] = useState([]);   // destination totals
  const [msSchemeData, setMsSchemeData] = useState([]); // scheme totals for drilldown
  const [msDrillDest, setMsDrillDest] = useState(null); // null = top level
  const [msLoading, setMsLoading] = useState(false);
  const [msExporting, setMsExporting] = useState(false);

  const MS_COLORS = [
    '#00d4ff','#00c97a','#f59e0b','#ff4560','#7c5cbf',
    '#ff7043','#26c6da','#66bb6a','#ef5350','#ab47bc',
    '#29b6f6','#ff7676',
  ];

  const buildMsParams = useCallback(() => {
    const p = new URLSearchParams({ fromdate: msFrom, todate: msTo });
    return p;
  }, [msFrom, msTo]);

  const loadMarketShare = useCallback(async () => {
    setMsLoading(true);
    setMsDestData([]);
    setMsSchemeData([]);
    setMsDrillDest(null);
    try {
      const rows = await api.get(`/reports/market-share/destinations?${buildMsParams()}`);
      // Filter to known destinations only
      const filtered = rows.filter(r => MARKET_SHARE_DESTINATIONS.includes(r.destination?.toLowerCase()));
      setMsDestData(filtered.map(r => ({ name: r.destination, value: Number(r.total) })));
    } catch (e) { console.error(e); }
    finally { setMsLoading(false); }
  }, [api, buildMsParams]);

  const drillDown = useCallback(async (destination) => {
    setMsLoading(true);
    setMsDrillDest(destination);
    try {
      const params = buildMsParams();
      params.set('destination', destination);
      const rows = await api.get(`/reports/market-share/schemes?${params}`);
      setMsSchemeData(rows.map(r => ({ name: r.scheme || '(unknown)', value: Number(r.total) })));
    } catch (e) { console.error(e); }
    finally { setMsLoading(false); }
  }, [api, buildMsParams]);

  const handleMsExport = useCallback(async () => {
    setMsExporting(true);
    try {
      const isDrill = !!msDrillDest;
      const exportData = isDrill ? msSchemeData : msDestData;
      const clean = isDrill
        ? exportData.map(r => ({ 'Destination': msDrillDest, 'Scheme': r.name, 'Count': r.value }))
        : exportData.map(r => ({ 'Destination': r.name, 'Count': r.value }));
      const filename = isDrill
        ? `market_share_${msDrillDest}_${msFrom}_to_${msTo}.xlsx`
        : `market_share_${msFrom}_to_${msTo}.xlsx`;
      await exportToExcel([{ name: 'Market Share', rows: clean }], filename);
    } catch (e) { alert('Export failed: ' + e.message); }
    finally { setMsExporting(false); }
  }, [msDestData, msSchemeData, msDrillDest, msFrom, msTo]);

  const msChartData = msDrillDest ? msSchemeData : msDestData;
  const msTotal = msChartData.reduce((s, d) => s + d.value, 0);

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <div className="page-subtitle">Historical data and logs</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t.id} className={`btn ${tab === t.id ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* Market Share tab */}
      {tab === 'marketshare' && (
        <>
          {/* Filters */}
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group">
                <label className="form-label">From Date</label>
                <input className="form-input" type="date" value={msFrom} onChange={e => setMsFrom(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">To Date</label>
                <input className="form-input" type="date" value={msTo} onChange={e => setMsTo(e.target.value)} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <button className="btn btn-primary" onClick={loadMarketShare} disabled={msLoading}>
                  {msLoading ? 'Loading…' : 'Generate'}
                </button>
                {msChartData.length > 0 && (
                  <button className="btn btn-secondary" onClick={handleMsExport} disabled={msExporting}>
                    {msExporting ? 'Exporting…' : '↓ Export Excel'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Chart area */}
          {msChartData.length > 0 && (
            <div className="card">
              {/* Breadcrumb */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <button
                  className={`btn btn-sm ${!msDrillDest ? 'btn-primary' : 'btn-ghost'}`}
                  onClick={() => { setMsDrillDest(null); setMsSchemeData([]); }}
                >
                  All Destinations
                </button>
                {msDrillDest && (
                  <>
                    <span style={{ color: 'var(--text-muted)' }}>›</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
                      {msDrillDest}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                      (click pie slice to explore schemes)
                    </span>
                  </>
                )}
                {!msDrillDest && (
                  <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    Click a destination slice to drill into schemes
                  </span>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'center' }}>
                {/* Pie */}
                <div>
                  <ResponsiveContainer width="100%" height={360}>
                    <PieChart>
                      <Pie
                        data={msChartData}
                        cx="50%" cy="50%"
                        innerRadius={80} outerRadius={140}
                        paddingAngle={2}
                        dataKey="value"
                        onClick={(entry) => {
                          if (!msDrillDest) drillDown(entry.name);
                        }}
                        style={{ cursor: msDrillDest ? 'default' : 'pointer' }}
                      >
                        {msChartData.map((_, i) => (
                          <Cell key={i} fill={MS_COLORS[i % MS_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v, name) => [
                          `${v.toLocaleString()} (${msTotal > 0 ? ((v / msTotal) * 100).toFixed(1) : 0}%)`,
                          name,
                        ]}
                        contentStyle={{
                          background: 'var(--bg-card)',
                          border: '1px solid var(--border-bright)',
                          borderRadius: 6, fontSize: 12,
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend table */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                    {msDrillDest ? `${msDrillDest.toUpperCase()} — Scheme Breakdown` : 'Destination Breakdown'}
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>
                          {msDrillDest ? 'Scheme' : 'Destination'}
                        </th>
                        <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>Count</th>
                        <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {msChartData.map((d, i) => (
                        <tr
                          key={i}
                          style={{ cursor: msDrillDest ? 'default' : 'pointer' }}
                          onClick={() => { if (!msDrillDest) drillDown(d.name); }}
                        >
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ width: 10, height: 10, borderRadius: '50%', background: MS_COLORS[i % MS_COLORS.length], flexShrink: 0 }} />
                              <span style={{ color: msDrillDest ? 'var(--text-primary)' : 'var(--accent)', fontWeight: msDrillDest ? 400 : 600 }}>
                                {d.name}
                              </span>
                            </div>
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-primary)' }}>
                            {d.value.toLocaleString()}
                          </td>
                          <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                            {msTotal > 0 ? ((d.value / msTotal) * 100).toFixed(1) : 0}%
                          </td>
                        </tr>
                      ))}
                      {/* Total row */}
                      <tr>
                        <td style={{ padding: '8px 8px 4px', fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Total</td>
                        <td style={{ padding: '8px 8px 4px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>
                          {msTotal.toLocaleString()}
                        </td>
                        <td style={{ padding: '8px 8px 4px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>100%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {msChartData.length === 0 && !msLoading && (
            <div className="card" style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
              Select a date range and click Generate to view the market share breakdown
            </div>
          )}
        </>
      )}

      {/* eScripting filters */}
      {tab === 'erxres' && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div className="form-group">
              <label className="form-label">From Date</label>
              <input className="form-input" type="date" value={erxFrom} onChange={e => setErxFrom(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">To Date</label>
              <input className="form-input" type="date" value={erxTo} onChange={e => setErxTo(e.target.value)} />
            </div>
            <div className="form-group" style={{ minWidth: 160 }}>
              <label className="form-label">Vendor</label>
              <select className="form-select" value={erxVendor} onChange={e => setErxVendor(e.target.value)}>
                <option value="">All Vendors</option>
                {ESCRIPTING_VENDORS.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <button className="btn btn-primary" onClick={loadErx} disabled={erxLoading}>
                {erxLoading ? 'Loading…' : 'Generate Report'}
              </button>
              <button className="btn btn-secondary" onClick={handleErxExport} disabled={erxExporting || erxLoading}>
                {erxExporting ? 'Exporting…' : '↓ Export Excel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transactions / Error Log filters */}
      {tab !== 'erxres' && tab !== 'marketshare' && tab !== 'invoice' && (
        <div className="card" style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            {tab !== 'errors' && (
              <>
                <div className="form-group">
                  <label className="form-label">From Date</label>
                  <input className="form-input" type="date" value={fromdate} onChange={e => setFrom(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">To Date</label>
                  <input className="form-input" type="date" value={todate} onChange={e => setTo(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Mailbox ID</label>
                  <input className="form-input" placeholder="Optional" value={mailboxid}
                    onChange={e => setMailboxId(e.target.value)} style={{ minWidth: 130 }} />
                </div>
                {tab === 'transactions' && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Destination</label>
                      <select className="form-select" value={destination}
                        onChange={e => setDestination(e.target.value)} style={{ minWidth: 130 }}>
                        <option value="">All</option>
                        {REPORT_DESTINATIONS.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Type</label>
                      <input className="form-input" placeholder="e.g. CLAIM" value={txtype}
                        onChange={e => setTxtype(e.target.value)} style={{ minWidth: 110 }} />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Scheme</label>
                      <input className="form-input" placeholder="e.g. BONITAS" value={scheme}
                        onChange={e => setScheme(e.target.value)} style={{ minWidth: 130 }} />
                    </div>
                  </>
                )}
              </>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <button className="btn btn-primary" onClick={load} disabled={loading}>
                {loading ? 'Loading…' : 'Generate Report'}
              </button>
              {tab === 'transactions' && (
                <button className="btn btn-secondary" onClick={handleExport} disabled={exporting || loading}
                  title="Exports full dataset without the 500 row screen limit">
                  {exporting ? 'Exporting…' : '↓ Export Excel'}
                </button>
              )}
            </div>
            {data.length > 0 && (
              <span style={{ fontSize: 12, color: 'var(--text-muted)', alignSelf: 'flex-end' }}>
                {data.length} records{data.length === 500 ? ' (screen limit — use Export for full data)' : ''}
              </span>
            )}
          </div>
        </div>
      )}

      {/* ── Invoice tab ─────────────────────────────────────────────────────── */}
      {tab === 'invoice' && (
        <>
          {/* Filters */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group">
                <label className="form-label">Year</label>
                <input className="form-input" type="number" value={invYear}
                  onChange={e => setInvYear(Number(e.target.value))}
                  style={{ width: 90 }} min={2020} max={2099} />
              </div>
              <div className="form-group">
                <label className="form-label">Month</label>
                <select className="form-select" value={invMonth} onChange={e => setInvMonth(Number(e.target.value))}>
                  {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Destination</label>
                <input className="form-input" value={invDest}
                  onChange={e => setInvDest(e.target.value)} style={{ width: 100 }} />
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <button className="btn btn-primary" onClick={loadInvoice} disabled={invLoading}>
                  {invLoading ? 'Loading…' : 'Generate Invoice'}
                </button>
                {invData && (
                  <>
                    <button className="btn btn-secondary" onClick={handleInvExport} disabled={invExporting}>
                      {invExporting ? 'Exporting…' : '↓ Export Excel'}
                    </button>
                    <button className="btn btn-secondary" onClick={handleInvPdfExport} disabled={invPdfExporting}>
                      {invPdfExporting ? 'Generating…' : '↓ Export PDF'}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Invoice display */}
          {invData && (
            <div className="card" style={{ padding: 0 }}>
              {/* Invoice header */}
              <div style={{
                padding: '28px 32px 20px',
                borderBottom: '1px solid var(--border)',
                background: 'var(--bg-base)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div style={{
                      display: 'inline-block', background: 'white',
                      borderRadius: 8, padding: '8px 16px', marginBottom: 12,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    }}>
                      <img src="/logo.png" alt="SmartSwitch" style={{ height: 28, display: 'block' }} />
                    </div>
                    <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em' }}>
                      Transaction Invoice
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                      Destination: <strong style={{ color: 'var(--text-primary)' }}>{invData.destination.toUpperCase()}</strong>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--accent)' }}>
                      {MONTHS[invData.month - 1]} {invData.year}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, fontFamily: 'var(--font-mono)' }}>
                      {invData.from} — {new Date(invData.to).toLocaleDateString('en-ZA')}
                    </div>
                    <div style={{ marginTop: 12, padding: '8px 16px', background: 'var(--bg-card)', borderRadius: 6, border: '1px solid var(--border)' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Applied Rate</div>
                      <div style={{ fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-mono)', color: 'var(--accent)' }}>
                        R {Number(invData.applicableRate).toFixed(4)}
                      </div>
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 2 }}>
                        per claim transaction
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Scheme summary table */}
              <div style={{ padding: '20px 32px' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
                  {invData.destination.toUpperCase()} — Scheme / Option Breakdown
                </div>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Scheme / Option</th>
                      <th>Plan Name</th>
                      <th style={{ textAlign: 'right' }}>Transaction Count</th>
                      <th style={{ textAlign: 'right' }}>Rate (R)</th>
                      <th style={{ textAlign: 'right' }}>Fee (R)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invData.schemes.map((s, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{s.scheme}</td>
                        <td style={{ color: 'var(--text-secondary)' }}>{s.plan_name || s.scheme}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                          {s.total.toLocaleString()}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                          {Number(invData.applicableRate).toFixed(4)}
                        </td>
                        <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--green)', fontWeight: 600 }}>
                          {s.fee.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                    {/* Total row */}
                    <tr style={{ borderTop: '2px solid var(--border-bright)', background: 'var(--bg-hover)' }}>
                      <td style={{ fontWeight: 800, fontSize: 14 }} colSpan={2}>TOTAL</td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 14 }}>
                        {invData.grandTotal.toLocaleString()}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>
                        {Number(invData.applicableRate).toFixed(4)}
                      </td>
                      <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: 16, color: 'var(--accent)' }}>
                        R {invData.totalFee.toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Volume tier note */}
                <div style={{ marginTop: 12, fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                  * Rate of R {Number(invData.applicableRate).toFixed(4)} applied based on {invData.grandTotal.toLocaleString()} total transactions for {MONTHS[invData.month - 1]} {invData.year}
                </div>
              </div>
            </div>
          )}

          {!invData && !invLoading && (
            <div className="card" style={{ textAlign: 'center', padding: '48px', color: 'var(--text-muted)' }}>
              Select a year and month then click Generate Invoice
            </div>
          )}
        </>
      )}

      {/* eScripting pie chart — vendor split */}
      {tab === 'erxres' && erxData.length > 0 && (() => {
        const ERX_COLORS = [
          '#00d4ff','#00c97a','#f59e0b','#ff4560','#7c5cbf',
          '#ff7043','#26c6da','#66bb6a','#ef5350','#ab47bc',
          '#29b6f6','#ff7676',
        ];
        // Aggregate totals per vendor across all mailboxes
        const vendorMap = {};
        erxData.forEach(r => {
          vendorMap[r.vendor] = (vendorMap[r.vendor] || 0) + Number(r.count);
        });
        const pieData = Object.entries(vendorMap)
          .map(([name, value], i) => ({ name: name.toLowerCase(), value, color: ERX_COLORS[i % ERX_COLORS.length] }))
          .sort((a, b) => b.value - a.value);
        const total = pieData.reduce((s, d) => s + d.value, 0);
        return (
          <div className="card" style={{ marginBottom: 20 }}>
            <div className="card-header">
              <span className="card-title">eScripting — Vendor Split</span>
              <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {total.toLocaleString()} total transactions
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: 24, alignItems: 'center' }}>
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%"
                    innerRadius={55} outerRadius={90}
                    paddingAngle={2} dataKey="value">
                    {pieData.map((_, i) => <Cell key={i} fill={pieData[i].color} />)}
                  </Pie>
                  <Tooltip
                    formatter={(v, name) => [`${v.toLocaleString()} (${total > 0 ? ((v/total)*100).toFixed(1) : 0}%)`, name]}
                    contentStyle={{ background: '#131a22', border: '1px solid #2a3f57', borderRadius: 6, fontSize: 12, color: '#e8f0fa' }}
                    labelStyle={{ color: '#7a9bbf' }}
                    itemStyle={{ color: '#e8f0fa' }}
                  />
                </PieChart>
              </ResponsiveContainer>
              {/* Legend table */}
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: 11, color: 'var(--text-muted)', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>Vendor</th>
                    <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>Count</th>
                    <th style={{ textAlign: 'right', fontSize: 11, color: 'var(--text-muted)', padding: '4px 8px', borderBottom: '1px solid var(--border)' }}>Share</th>
                  </tr>
                </thead>
                <tbody>
                  {pieData.map((d, i) => (
                    <tr key={i}>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 10, height: 10, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
                          <span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{d.name}</span>
                        </div>
                      </td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-primary)' }}>
                        {d.value.toLocaleString()}
                      </td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--border)', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>
                        {total > 0 ? ((d.value / total) * 100).toFixed(1) : 0}%
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td style={{ padding: '7px 8px', fontWeight: 700, color: 'var(--text-secondary)', fontSize: 12 }}>Total</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--text-primary)', fontSize: 13 }}>{total.toLocaleString()}</td>
                    <td style={{ padding: '7px 8px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-muted)' }}>100%</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* Results */}
      <div className="card" style={{ padding: 0 }}>
        <div className="table-container" style={{ maxHeight: 500, overflow: 'auto' }}>
          {tab === 'transactions' && (
            <table className="data-table">
              <thead>
                <tr><th>Date/Time</th><th>BHF</th><th>Mailbox</th><th>Tx ID</th><th>Type</th><th>Result</th><th>Destination</th><th>Scheme</th><th>Claim</th></tr>
              </thead>
              <tbody>
                {loading ? [...Array(5)].map((_, i) => <tr key={i}>{[...Array(8)].map((_, j) => <td key={j}><div className="skeleton" style={{ height: 12, width: '80%' }} /></td>)}</tr>)
                  : data.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>Run a query to see results</td></tr>
                  : data.map(r => (
                    <tr key={r.txid}>
                      <td>{new Date(r.txdatetime).toLocaleString()}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{r.bhf || '—'}</td>
                      <td style={{ color: 'var(--accent)' }}>{r.mailboxname || r.mailboxid}</td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 11 }}>{r.txid}</td>
                      <td>{r.txtype}</td>
                      <td><span className={`badge ${r.txresult === '0' || r.txresult === 'OK' || r.txresult === '' ? 'badge-success' : 'badge-error'}`}>{r.txresult || 'OK'}</span></td>
                      <td>{r.going_to || '—'}</td>
                      <td>{r.going_to_sub || '—'}</td>
                      <td>{r.claim ? `R ${parseFloat(r.claim).toFixed(2)}` : '—'}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}

          {tab === 'erxres' && (() => {
            if (erxLoading) return (
              [...Array(5)].map((_, i) => (
                <tr key={i}>{[...Array(5)].map((_, j) => <td key={j}><div className="skeleton" style={{ height: 12, width: '80%' }} /></td>)}</tr>
              ))
            );
            if (erxData.length === 0) return (
              <table className="data-table"><tbody>
                <tr><td style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>Run a query to see results</td></tr>
              </tbody></table>
            );

            // Derive unique vendors from results, sorted
            const vendors = [...new Set(erxData.map(r => r.vendor))].sort();

            // Pivot: group rows by mailbox, keep raw vendor rows for drill-down
            const mailboxMap = {};
            erxData.forEach(r => {
              const key = r.mailbox_name || r.bhf || '';
              if (!mailboxMap[key]) mailboxMap[key] = { mailbox_name: r.mailbox_name, bhf: r.bhf, vendors: {}, rows: [] };
              mailboxMap[key].vendors[r.vendor] = Number(r.count);
              mailboxMap[key].rows.push(r);
            });
            const mailboxRows = Object.values(mailboxMap);

            // Grand totals per vendor
            const vendorTotals = {};
            vendors.forEach(v => {
              vendorTotals[v] = mailboxRows.reduce((s, m) => s + (m.vendors[v] || 0), 0);
            });
            const grandTotal = Object.values(vendorTotals).reduce((s, v) => s + v, 0);

            return (
              <ErxTable
                mailboxRows={mailboxRows}
                vendors={vendors}
                vendorTotals={vendorTotals}
                grandTotal={grandTotal}
                erxFrom={erxFrom}
                erxTo={erxTo}
                api={api}
                buildErxParams={buildErxParams}
              />
            );
          })()}

          {tab === 'errors' && (
            <table className="data-table">
              <thead><tr><th>Date/Time</th><th>Mailbox</th><th>Message</th></tr></thead>
              <tbody>
                {loading ? [...Array(5)].map((_, i) => <tr key={i}>{[...Array(3)].map((_, j) => <td key={j}><div className="skeleton" style={{ height: 12, width: '80%' }} /></td>)}</tr>)
                  : data.length === 0 ? <tr><td colSpan={3} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '32px' }}>No error records found</td></tr>
                  : data.map((r, i) => (
                    <tr key={i} style={{ background: 'rgba(255,69,96,0.04)' }}>
                      <td style={{ whiteSpace: 'nowrap', color: 'var(--text-primary)', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                        {r.date || '—'}
                      </td>
                      <td style={{ color: 'var(--accent)' }}>
                        {r.mailboxname || r.mailboxid || '—'}
                      </td>
                      <td style={{ color: 'var(--red)', fontSize: 12 }}>
                        {r.message || '—'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
